import { Injectable, NgZone } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import {
  EMAIL_DESTINATION,
  ERROR_MAPPING,
  PAYMENT_REQUEST_PAYMENT_CARD_DEFAULT,
  PaymentMethodsType,
  SMS_DESTINATION,
} from '@common/helpers/constants/service-request-payment-input/service-request-payment-input.constants';
import { FEATURE_TO_ROUTE_MAPPING } from '@common/helpers/constants/servicing.contant';
import { onHandleCommonNo12 } from '@common/helpers/my-booking';
import { ServiceInfoData } from '@common/interfaces/mybooking/mybooking.interface';
import {
  ApiResponse,
  CardinalResponse,
  CardinalResultInfo,
  SetupCompleteData,
} from '@common/interfaces/service-request-payment-input/service-request-payment-input.interface';
import {
  ServiceRequestCompletedState,
  WarningMessageData,
} from '@common/store/service-request-completed/service-request-completed.state';
import { SERVICING_ROUTES } from '@conf/routes.config';
import { environment } from '@env/environment';
import { SupportClass } from '@lib/components/support-class';
import { ApiCommonRequest, ErrorType, LogType, PageType } from '@lib/interfaces';
import {
  AswMasterService,
  AswServiceStoreService,
  CommonLibService,
  LoadScriptService,
  LoggerDatadogService,
  PageLoadingService,
} from '@lib/services';
import { BehaviorSubject, firstValueFrom, fromEvent, interval, Subscription, timer } from 'rxjs';
import { AmcmemberApiService, GetBasicReservationInformationRequest } from 'src/sdk-amcmember';
import {
  CreateMycarValetResponseWarningsInner,
  GetOrderResponseDataOrderType,
  PaymentRequestAlternativePaymentMethod,
  PaymentRequestAuthorization,
  PaymentRequestAuthorizationPaymentResumption,
  PaymentRequestAuthorizationPaymentResumptionLink,
  PaymentRequestAuthorizationPaymentResumptionLinkPayloadInner,
  PaymentRequestPaymentCard,
  Traveler,
  Type1,
  UpdateServicesRequest,
  UpdateServicesRequestContactTravelersInner,
  UpdateServicesResponse,
  UpdateServicesResponseDataLinkPayloadInner,
} from 'src/sdk-servicing';
import { MyBookingServiceService } from '../my-booking/my-booking-service/my-booking-service.service';
import { ServiceRequestCompletedStoreService } from '../service-request-completed/service-request-completed.store.service';
import { ServicingCommonStoreService } from '../servicing-common-store/servicing-common-store.service';
import { ServiceRequestPaymentInputService } from './service-request-payment-input-service';
import { ServiceRequestPaymentInputStoreService } from './service-request-payment-input-store.service';
import { isStringEmpty, parseEmptyArray, stringEmptyDefault } from '@common/helpers';
import { ErrorCodeConstants } from '@conf/app.constants';

@Injectable({ providedIn: 'root' })
export class CardinalService extends SupportClass {
  ordersPaymentRecordsReqParam: UpdateServicesRequest = {} as UpdateServicesRequest;
  is3DSecureApplied: boolean = false;

  // 3DS認証結果情報
  response3DS: CardinalResultInfo = {};

  resultHandleCommonNo5: number = 0;

  // サービス情報登録API実行結果
  resultExcuteApi3!: UpdateServicesResponse;

  // CardinalセッションID
  public _cardinalSessionId: string = '';

  private _isPaying$: BehaviorSubject<boolean> = new BehaviorSubject(false);

  // action: handle for the first AMOP and Cardinal
  private responseUpdateService: UpdateServicesResponse = {};

  /**
   * AMOP支払中状態を取得する
   */
  public getPayingStatus$() {
    return this._isPaying$.asObservable();
  }

  constructor(
    private readonly _common: CommonLibService,
    private readonly _aswMasterService: AswMasterService,
    private readonly _amcmemberApiService: AmcmemberApiService,
    private readonly _myBookingServiceService: MyBookingServiceService,
    private readonly _aswServiceStoreService: AswServiceStoreService,
    private readonly _serviceRequestCompletedStoreService: ServiceRequestCompletedStoreService,
    private readonly _serviceRequestPaymentInputStoreService: ServiceRequestPaymentInputStoreService,
    private readonly _serviceRequestPaymentInputService: ServiceRequestPaymentInputService,
    private readonly _servicingCommonStoreService: ServicingCommonStoreService,
    private readonly _router: Router,
    private readonly _loadScriptService: LoadScriptService,
    private readonly _loggerDatadogService: LoggerDatadogService,
    private readonly _ngZone: NgZone,
    private readonly _loadingSvc: PageLoadingService
  ) {
    super();
  }

  /**
   * 購入発券API
   * @param paymentInputFG
   * @param deviceId
   * @returns param for first call API
   */
  createUpdateServicesRequest1(
    serviceInfoData: ServiceInfoData,
    paymentInputFG: FormGroup,
    deviceId: string
  ): UpdateServicesRequest {
    let payerIdentification = {};
    // Default value
    let alternativePaymentMethod: PaymentRequestAlternativePaymentMethod | undefined = undefined;
    let paymentCard = {};
    let confirmationEmail = undefined;
    let isUpdateBasicPaymentMethod = undefined;
    let receiptAddress = undefined;
    let travelers: UpdateServicesRequestContactTravelersInner[] = [];

    // 画面.支払方法選択エリア.支払方法="CD"(クレジットカード)場合、以下の項目を設定する
    if (this._serviceRequestPaymentInputService.paymentMethod$.value === PaymentMethodsType.CREDIT_CARD) {
      const result = this.onHandleCreditCard(paymentInputFG, deviceId);
      paymentCard = result.paymentCard;
      payerIdentification = result.payerIdentification;
      confirmationEmail = paymentInputFG.get('cardHolderConfirmEmail')?.value;
      isUpdateBasicPaymentMethod = paymentInputFG.get('saveAsUsualCheckboxControl')?.value;
      receiptAddress = paymentInputFG.get('issuerReceiptEmail')?.value;
    }

    // 選択中支払方法の値が、以下のいずれかに該当する場合、設定する。
    // 1. ”AP”(Alipay)
    // 2. ”CU”(銀聯)
    // 3. ”PP”(PayPal)
    else if (
      this._serviceRequestPaymentInputService.paymentMethod$.value === PaymentMethodsType.ALIPAY ||
      this._serviceRequestPaymentInputService.paymentMethod$.value === PaymentMethodsType.UNION_PAY ||
      this._serviceRequestPaymentInputService.paymentMethod$.value === PaymentMethodsType.PAYPAL
    ) {
      alternativePaymentMethod = {
        paymentMethod: this.onHandleAMOP(),
      };
    }

    travelers = this.onPrepareTravelersParam(paymentInputFG);

    const isContact =
      this._serviceRequestPaymentInputService.pnr$.value?.orderType?.channelType ===
        GetOrderResponseDataOrderType.ChannelTypeEnum.Gds ||
      this._serviceRequestPaymentInputService.pnr$.value?.orderType?.channelType ===
        GetOrderResponseDataOrderType.ChannelTypeEnum.Nh3;
    const request: UpdateServicesRequest = {
      orderId: this._serviceRequestPaymentInputService.pnr$.value.orderId,
      credential: {
        firstName: this._aswServiceStoreService.aswServiceData?.firstName ?? '',
        lastName: this._aswServiceStoreService.aswServiceData?.lastName ?? '',
      },
      paymentRequest: {
        ...paymentCard,
        ...payerIdentification,
        alternativePaymentMethod,
      },
      receiptAddress: receiptAddress,
      ...(isContact && {
        contact: {
          confirmationEmail: confirmationEmail,
          travelers: travelers,
        },
      }),
      isUpdateBasicPaymentMethod: isUpdateBasicPaymentMethod,
      // サービス情報データ.サービス申込情報リスト
      services: serviceInfoData.services,
      // サービス情報データ.座席指定情報
      seats: serviceInfoData.seats,
    };

    return request;
  }

  private onPrepareTravelersParam(paymentInputFG: FormGroup): UpdateServicesRequestContactTravelersInner[] {
    const listTravelrs = this._serviceRequestPaymentInputService.pnr$.value?.travelers ?? [];
    const travelers: UpdateServicesRequestContactTravelersInner[] = [];
    for (let traveler of listTravelrs) {
      const travelerId = traveler.id;
      let email = '';
      let numberCountry = '';
      let number = '';
      const emailDestination = paymentInputFG.get('emailDestination_' + travelerId)?.value;

      if (emailDestination === EMAIL_DESTINATION.SAME_AS_APPLICATION_COMPLETION_CONTACT_EMAIL_ADDRESS)
        email = paymentInputFG.get('notificationConfirmationEmail')?.value;
      if (emailDestination === EMAIL_DESTINATION.SEND_TO_INDIVIDUAL)
        email = paymentInputFG.get('emailAddress_' + travelerId)?.value;
      // condition to check edit area
      if (
        travelerId &&
        this._serviceRequestPaymentInputService.travelerCanEditInputNotificationSMS$.value?.has(travelerId)
      ) {
        numberCountry = this._serviceRequestPaymentInputService.phoneCountryCode;
        number = paymentInputFG.get('number_' + travelerId)?.value;
      }
      travelers.push({
        travelerId: travelerId,
        ...(email && {
          email: {
            address: email,
          },
        }),
        ...(numberCountry &&
          number && {
            phone: {
              numberCountry,
              number,
            },
          }),
      });
    }
    return travelers;
  }

  private onHandleAMOP() {
    switch (this._serviceRequestPaymentInputService.paymentMethod$.value) {
      case PaymentMethodsType.ALIPAY:
        return PaymentRequestAlternativePaymentMethod.PaymentMethodEnum.Alipay;
      case PaymentMethodsType.UNION_PAY:
        return PaymentRequestAlternativePaymentMethod.PaymentMethodEnum.ChinaUnionPay;
      case PaymentMethodsType.PAYPAL:
        return PaymentRequestAlternativePaymentMethod.PaymentMethodEnum.Paypal;
      default:
        return '' as PaymentRequestAlternativePaymentMethod.PaymentMethodEnum;
    }
  }
  private onHandleCreditCard(paymentInputFG: FormGroup, deviceId: string) {
    let registeredCardType = {};
    let cardNumber = {};
    let cvv = {};
    let paymentCard = {};
    let payerIdentification = {};
    const isUatpCard = paymentInputFG.get('uATPCardChecked')?.value;
    const holderName = paymentInputFG.get('holderName')?.value;
    const expiryDate = paymentInputFG.get('expiryDate')?.value;
    const saveAsBasicReservationInformation = paymentInputFG.get('saveAsBasicReservationInformation')?.value;
    const emailAddress = paymentInputFG.get('cardHolderEmail')?.value;
    const phoneCountry = this._serviceRequestPaymentInputService.cardHolderCountryPhoneExtension;
    const phoneNumber = paymentInputFG.get('cardHolderPhoneNumber')?.value;

    if (
      this._serviceRequestPaymentInputService.selectedCard ===
        PaymentRequestPaymentCard.RegisteredCardTypeEnum.PaymentCard1 ||
      this._serviceRequestPaymentInputService.selectedCard ===
        PaymentRequestPaymentCard.RegisteredCardTypeEnum.PaymentCard2
    ) {
      registeredCardType = { registeredCardType: this._serviceRequestPaymentInputService.selectedCard };
    }
    if (this._serviceRequestPaymentInputService.selectedCard === PAYMENT_REQUEST_PAYMENT_CARD_DEFAULT.NEW_CARD) {
      cardNumber = { cardNumber: paymentInputFG.get('cardNumber')?.value };
    }
    if (isUatpCard === false) {
      cvv = { cvv: paymentInputFG.get('cvv')?.value };
    }

    paymentCard = {
      paymentCard: {
        ...registeredCardType,
        ...cardNumber,
        holderName: holderName ?? '',
        expiryDate: expiryDate ?? '',
        ...cvv,
        isUatpCard: isUatpCard,
        saveAsBasicReservationInformation: saveAsBasicReservationInformation,
      },
    };
    payerIdentification = {
      payerIdentification: {
        deviceFingerPrint: deviceId,
        contact: {
          email: {
            address: emailAddress ?? '',
          },
          phone: {
            countryPhoneExtension: phoneCountry ?? '',
            number: phoneNumber ?? '',
          },
        },
      },
    };
    return {
      paymentCard,
      payerIdentification,
    };
  }
  /**
   * Create param for call API 2th
   * 購入発券API(2回目)
   * @returns param for call API 2th
   */
  createUpdateServicesRequest2(): UpdateServicesRequest {
    const authorization: PaymentRequestAuthorization = {
      paymentRedirectionContext: {
        partner3DSSessionId: this._cardinalSessionId,
      },
    };
    if (this.ordersPaymentRecordsReqParam.paymentRequest) {
      if (this.ordersPaymentRecordsReqParam.paymentRequest.payerIdentification) {
        this.ordersPaymentRecordsReqParam.paymentRequest.payerIdentification = {
          ...this.ordersPaymentRecordsReqParam.paymentRequest.payerIdentification,
          browser: {
            acceptHeader: 'Accept: text/plain; q=0.5, text/html',
            userAgent: navigator.userAgent,
          },
        };
      }
      this.ordersPaymentRecordsReqParam.paymentRequest = {
        ...this.ordersPaymentRecordsReqParam.paymentRequest,
        authorization: authorization,
      };
    }
    return this.ordersPaymentRecordsReqParam;
  }

  /**
   * Create param for call API 3th
   * 購入発券API(3回目)
   * @param response2 response from API 2th
   * @returns param for call API 3th
   */
  createUpdateServicesRequest3(response2: ApiResponse): UpdateServicesRequest {
    let paymentResumption: PaymentRequestAuthorizationPaymentResumption = {};
    // 3DSv1認証が必要
    if (this._searchWarningsCode(response2?.model?.warnings, ErrorCodeConstants.ERROR_CODES.WBAZ000224)) {
      paymentResumption = { payerAuthenticationResponse: this.response3DS.jwtToken };
    }

    // 3DSv2認証が必要
    if (this._searchWarningsCode(response2?.model?.warnings, ErrorCodeConstants.ERROR_CODES.WBAZ000225)) {
      paymentResumption = { challenge3DSResponse: this.response3DS.jwtToken };
    }
    if (this.ordersPaymentRecordsReqParam.paymentRequest) {
      this.ordersPaymentRecordsReqParam.paymentRequest.authorization = {
        ...this.ordersPaymentRecordsReqParam.paymentRequest.authorization,
        paymentResumption: paymentResumption,
      };
    }
    return this.ordersPaymentRecordsReqParam;
  }

  /**
   * Post-AMOP authentication processing
   * 購入発券API （2回目）
   * @param link 支払認証用HTTPリクエスト情報
   * @returns param for call API 2th
   */
  private _createPaymentRecordsRequestParametersAmop(
    link: PaymentRequestAuthorizationPaymentResumptionLink
  ): UpdateServicesRequest {
    const authorization: PaymentRequestAuthorization = {
      paymentResumption: {
        link: link,
      },
    };
    this.ordersPaymentRecordsReqParam.paymentRequest!.authorization = authorization;
    return this.ordersPaymentRecordsReqParam;
  }

  async excutePaymentInput(deviceId: string) {
    this._loadingSvc.startLoading(true);
    // (14.4) 以下のように用意する。
    // (14.4.1)	予約詳細(サービス)(S01-P033)の[サービス情報登録APIのデータ用意処理]を実施して、返却値のサービス情報データを取得する。
    const param = {
      myBookingServiceInformation: this._servicingCommonStoreService.servicingCommon,
      mybookingServiceState: {
        pnr: this._serviceRequestPaymentInputService.pnr$.value,
        firstBaggageBoundInforList: this._serviceRequestPaymentInputService.firstBaggageBoundInforList,
        firstBaggagePassengerInforList: this._serviceRequestPaymentInputService.firstBaggagePassengerInforList,
        loungeFlightInforList: this._serviceRequestPaymentInputService.loungeFlightInforList,
        loungePassengerInforList: this._serviceRequestPaymentInputService.loungePassengerInforList,
        mealFlightInforList: this._serviceRequestPaymentInputService.mealFlightInforList,
        mealPassengerInforList: this._serviceRequestPaymentInputService.mealPassengerInforList,
        seatFlightInforList: this._serviceRequestPaymentInputService.seatFlightInforList,
        seatPassengerInforList: this._serviceRequestPaymentInputService.seatPassengerInforList,
      },
    };
    // (14.4.1)	予約詳細(サービス)(S01-P033)の[サービス情報登録APIのデータ用意処理]を実施して、返却値のサービス情報データを取得する。
    const serviceInfoData = onHandleCommonNo12(param);
    // (14.4.3) 3Dセキュア認証実施有無
    this.is3DSecureApplied = false;
    // (14.5) サービス情報登録APIを呼び出す。（1回目）
    // (14.5.1) リクエスト情報
    this.ordersPaymentRecordsReqParam = this.createUpdateServicesRequest1(
      serviceInfoData,
      this._serviceRequestPaymentInputService.paymentInputFG,
      deviceId
    );

    const response: ApiResponse = await this._serviceRequestPaymentInputService.postOrdersUpdateServicesPost(
      this.ordersPaymentRecordsReqParam
    );
    // (14.5.2)	レスポンス情報
    // (14.5.2.1)	正常終了の場合
    if (response.model) {
      // (14.5.2.1.1)	サービス情報登録API応答(1回目).warningsの中に、code = "WBAZ000223"(Cardinalへの接続が必要)が含まれる場合、
      if (this._searchWarningsCode(response.model?.warnings, ErrorCodeConstants.ERROR_CODES.WBAZ000223)) {
        this.responseUpdateService = response.model;
        this._preCardinalAuthentication1(response);
      }
      // (14.5.2.1.2)	サービス情報登録API応答(1回目).warningsの中に、code = "WBAZ000226"(AMOPによる支払にて認証が必要)が含まれる場合、
      else if (this._searchWarningsCode(response.model?.warnings, ErrorCodeConstants.ERROR_CODES.WBAZ000226)) {
        this.responseUpdateService = response.model;
        // AMOP認証前処理
        this._preAmopAuthentication1(response);
      }
      // (14.5.2.1.3) サービス情報登録API応答(1回目).warningsの中に、code = "WBAZ000564"(予約基本情報のクレジットカード情報の登録時、いつもの情報クレジットカード1およびいつもの情報クレジットカード2のクレジットカード番号が両方存在する。)が含まれる場合、
      // 7.の処理へジャンプ（移動）する
      else if (this._searchWarningsCode(response.model?.warnings, ErrorCodeConstants.ERROR_CODES.WBAZ000564)) {
        this.responseUpdateService = response.model;
        // (14.7), (14.8), (14.9)
        this._informationTransferProcessing(response.model);
        return;
      } else {
        // (14.5.2.1.4)	上記以外の場合、以下の処理を行う。
        // (14.5.2.1.4.1)	サービス情報登録API実行結果にサービス情報登録API応答(1回目)を設定する。
        // (14.5.2.1.4.2)	以下のパラメータで[当画面共通処理No5.サービス情報登録API実行結果処理]を実施する。
        const resultHandleCommonNo5 = this._serviceRequestPaymentInputService.handleCommonNo5(response);
        if (resultHandleCommonNo5 === 0) {
          this._postCompletion(response.model ?? {});
        } else {
          // (14.5.2.1.4.3)	以下のパラメータで[当画面共通処理No5.サービス情報登録API実行結果処理]を実施する。
          this._loadingSvc.endLoading();
          this.responseUpdateService = {};
          return;
        }
      }
    } else {
      // (14.5.2.2)	異常終了の場合
      // (14.5.2.2.1)	[当画面共通処理No5.サービス情報登録API実行結果処理]を実施する。
      const resultHandleCommonNo5 = this._serviceRequestPaymentInputService.handleCommonNo5(response);
      // (14.5.2.2.2)	戻り値= -1(異常終了)の場合、当処理を終了する。
      if (resultHandleCommonNo5 === -1) {
        this._loadingSvc.endLoading();
        this.responseUpdateService = {};
        return;
      }
    }
  }

  /**
   * 3DS1 authentication preprocessing
   * @param response update service api response
   */
  private async _preCardinalAuthentication1(response: any) {
    this._loadScriptService
      .load$(this._aswMasterService.getMPropertyByKey('cardinal', 'script.url'), true)
      .subscribe((path: any) => {
        if (path) {
          // (14.5.2.1.1.3)	以下のパラメータで[当画面共通処理No4.Cardinal初期設定処理]を実施する。
          // Cardinal.configure
          this._handleCommonNo4(response);

          const timeSubscribe = this._cardinalClientTimeoutHandle();
          // Cardinalのコールバック(Carcinal.setupComplete)により実行される処理
          (window as any).Cardinal.on('payments.setupComplete', (setupCompleteData: SetupCompleteData) => {
            // 4.1 セットアップ完了タイマを破棄する
            timeSubscribe.unsubscribe();
            this._ngZone.run(() => {
              this._cardinalResponseEvent(setupCompleteData);
            });
          });
        } else {
          this._common.errorsHandlerService.setRetryableError(PageType.PAGE, {
            errorMsgId: 'XXX',
          });
          this._loadingSvc.endLoading();
        }
      });
  }

  /**
   * Open the second Cardinal Authentication modal
   * @param response2 response of the second updateService API call
   */
  private _preCardinalAuthentication2(response2: ApiResponse) {
    // (14.5.2.1.1.9.2.1.1.1)	3Dセキュア認証実施有無にtrueを設定する。
    this.is3DSecureApplied = true;
    // (14.5.2.1.1.9.2.1.1.4)	以下の処理にて、パラメータ(JSON形式)でCardinal.continueを実行する。
    this._cardinalContinue(response2);
    this._cardinalValidated(response2);
  }

  /**
   * Processing code entry for DS authentication
   * #DS認証の認証コード入力後の処理
   */
  private async _cardinalAuthorizationPostProcessing(response2: ApiResponse) {
    // (14.5.2.1.1.9.2.1.1.7)	3DS認証結果情報.キャンセル判定が”01”(ユーザが認証をキャンセルした)の場合、処理を中断する。
    if (this.response3DS.challengeCancel === '01') {
      // Cancel user authentication
      this._loadingSvc.endLoading();
      return;
    }

    // (14.5.2.1.1.9.2.1.1.8)	以下の内容をチェックする。
    const errorNumber = Number(this._aswMasterService.getMPropertyByKey('cardinal', 'errorNumberList.success'));
    const errorDescriptioPatern = new RegExp(
      this._aswMasterService.getMPropertyByKey('cardinal', 'errorDescriptionRegex.success')
    );

    // (14.5.2.1.1.9.2.1.1.8.3.1)	3DS認証結果情報.エラーコードが3DS認証成功コードリストに含まれない
    // (14.5.2.1.1.9.2.1.1.8.3.2)	3DS認証結果情報.エラー詳細が3DS認証成功詳細パターンに含まれない
    // (14.5.2.1.1.9.2.1.1.8.3.3)	3DS認証結果情報.JWTトークンが存在しない
    if (
      errorNumber !== this.response3DS.errorNumber ||
      !errorDescriptioPatern.test(this.response3DS.errorDescription ?? '') ||
      !this.response3DS.jwtToken
    ) {
      this._loadingSvc.endLoading();
      this._common.errorsHandlerService.setRetryableError(PageType.PAGE, { errorMsgId: 'E0397' });
      return;
    }

    // (14.5.2.1.1.9.2.1.1.9)	サービス情報登録APIを呼び出す。（3回目）
    const updateServicesRequest3 = this.createUpdateServicesRequest3(response2);
    // (14.5.2.1.1.9.2.1.1.9.1)	リクエスト情報
    const response3 = await this._serviceRequestPaymentInputService.postOrdersUpdateServicesPost(
      updateServicesRequest3
    );
    // (14.5.2.1.1.9.2.1.1.9.2.3)	以下のパラメータで[当画面共通処理No5.サービス情報登録API実行結果処理]を実施する。
    const resultHandleCommonNo5 = this._serviceRequestPaymentInputService.handleCommonNo5(response3);
    if (resultHandleCommonNo5 === 0) {
      // (14.5.2.1.1.9.2.1.1.9.2.1)	正常終了の場合
      this._postCompletion(response3.model ?? {});
    } else if (resultHandleCommonNo5 === -1) {
      // (14.5.2.1.1.9.2.1.1.9.2.4)	戻り値= -1(異常終了)の場合、当処理を終了する。
      this._loadingSvc.endLoading();
      return;
    }
  }
  /**
   * Final step processing after completing cardinal and AMOP
   * @param responseUpdateService response of the final updateService API call
   */
  async _postCompletion(responseUpdateService: UpdateServicesResponse) {
    // (14.6) ユーザ共通情報.POS国コード ≠ "IN"(インド) かつ 画面.予約基本情報のいつもの支払方法登録チェックがチェックONの場合
    if (
      this._common.aswContextStoreService.aswContextData.posCountryCode !== 'IN' &&
      this._serviceRequestPaymentInputService.paymentInputFG.get('saveAsUsualCheckboxControl')?.value
    ) {
      // (14.6.1) 予約基本情報取得APIを呼び出す。
      const param: ApiCommonRequest & GetBasicReservationInformationRequest = {
        commonIgnoreErrorFlg: true,
      };
      const res = this._amcmemberApiService.getBasicReservationInformationPost(param);
      // (14.6.1.1) リクエスト情報
      const responseBasicReservationInformation = await firstValueFrom(res);
      // (14.6.1.2.1.1)	取得した予約基本情報取得API応答.data配下の項目の値で、会員情報の同項目の値を更新する。
      this._common.amcMemberStoreService.updateAMCMemberByKey(
        'amcNumber',
        responseBasicReservationInformation.data?.amcNumber
      );
    }
    // (14.7), (14.8), (14.9)
    this._informationTransferProcessing(responseUpdateService);
  }

  private _preAmopAuthentication1(response: ApiResponse) {
    this._isPaying$.next(true);
    // (14.5.2.1.2.1)	iFrameをモーダル表示する。
    // (14.5.2.1.2.2)	iFrame内に空白画面(外部サイトへ遷移する際に一時的に表示する空白のページ)を表示する。
    // (14.5.2.1.2.4)	空白画面にて、画面を表示した回数をカウントし、2回表示された場合はブラウザバックにて戻ったと見做し、iframeを閉じる。
    this.subscribeService('amopPostMessage', fromEvent<MessageEvent>(window, 'message'), (message: MessageEvent) => {
      console.log('recieve message');
      // 送信元のオリジンと現在のドメインが一致しない場合は処理しない
      if (window.location.origin !== message.origin) {
        return;
      }

      // データ内のメッセージタイプが amop以外は処理しない
      if (message.data?.messageType !== 'amop') {
        return;
      }
      // CONNECTION_KIND=ZZZを追加したサービス情報登録API応答(1回目).data.link.hrefのURLへ遷移する。(外部サイト)
      // ※一応Paypalページへ遷移する場合、CONNECTION_KIND=ZZZを追加しない
      let messageURL = message.data.url;
      if (this._serviceRequestPaymentInputService.paymentMethod$.value !== PaymentMethodsType.PAYPAL) {
        messageURL = this.updateMessageURLWithParam(messageURL, 'CONNECTION_KIND', 'ZZZ');
      }
      let paymentResumptionLink: PaymentRequestAuthorizationPaymentResumptionLink = { href: '', method: 'GET' };
      // 引数のパラメーターが入っている場合
      if (message.data?.params && Object.keys(message.data?.params).length > 0) {
        paymentResumptionLink = {
          href: message.data.url,
          method: message.data.method,
          payload: Object.entries(message.data.params).map(
            ([key, value]) =>
              ({
                name: key,
                value: value as string,
              } as PaymentRequestAuthorizationPaymentResumptionLinkPayloadInner)
          ),
        };
      }
      this.deleteSubscription('amopPostMessage');
      // 5.Alipay、銀聯、PayPal認証後処理を行う。
      this._preAmopAuthentication2(paymentResumptionLink);
    });
    // フォーム作成
    this._createAmopForm(response);
  }

  /**
   * AMOP post-authentication process
   * @param link
   * @returns
   */
  private async _preAmopAuthentication2(link: PaymentRequestAuthorizationPaymentResumptionLink) {
    // (14.5.2.1.2.7)	画面.支払方法選択エリア.支払方法="PP"(PayPal)かつ 認証結果情報.parametersのname="PayerID"の件数=0の場合、処理を終了する。
    if (this._serviceRequestPaymentInputService.paymentMethod$.value === PaymentMethodsType.PAYPAL) {
      if ((link.payload?.filter((parameter) => parameter.name === 'PayerID').length ?? 0) === 0) {
        this._isPaying$.next(false);
        this._loadingSvc.endLoading();
        return;
      }
    }

    // (14.5.2.1.2.8)	サービス情報登録APIを呼び出す。（2回目）
    const params = this._createPaymentRecordsRequestParametersAmop(link);
    // (14.5.2.1.2.8.2.2) 異常終了の場合
    const responseAMOP = await this._serviceRequestPaymentInputService.postOrdersUpdateServicesPost(params);
    // (14.5.2.1.2.8.2.3) 以下のパラメータで[当画面共通処理No5.サービス情報登録API実行結果処理]を実施する。
    const resultHandleCommonNo5 = this._serviceRequestPaymentInputService.handleCommonNo5(responseAMOP);
    // 914.5.2.1.2.8.2.4)	戻り値= -1(異常終了)の場合、当処理を終了する。
    if (resultHandleCommonNo5 === 0) {
      this._postCompletion(responseAMOP.model ?? {});
    } else {
      this._isPaying$.next(false);
      // (14.5.2.1.2.8.2.4) 戻り値= -1(異常終了)の場合、当処理を終了する。
      this._loadingSvc.endLoading();
    }
  }

  private _createAmopForm(response: ApiResponse): void {
    let url: string = '';
    let payload: Array<UpdateServicesResponseDataLinkPayloadInner> = [];
    let method: string = '';

    // payloadを取得する
    // payloadが入っていない場合はクエリストリングを分解して取得する
    if (!response.model?.data?.link?.payload || response.model?.data?.link?.payload?.length === 0) {
      // urlを分解
      const splitUrl: string[] = response.model?.data?.link?.href?.split('?') as string[];

      // url取得
      url = splitUrl[0];

      // urlを分解してクエリストリングがとれている場合
      if (splitUrl.length > 1) {
        // クエリストリングを取得してパラメーターをKeyValue形式にする
        splitUrl[1].split('&').forEach((parameter: string) => {
          const items = parameter.split('=');
          const item: UpdateServicesResponseDataLinkPayloadInner = {
            name: items[0],
            value: items[1],
          };
          payload.push(item);
        });
      }

      // メソッド取得
      method = response.model?.data?.link?.method as any;
    } else {
      // url取得
      url = response.model?.data?.link?.href ?? '';

      // payloadが入っている場合はその値を取得
      payload = response.model?.data?.link?.payload ?? [];

      // メソッド取得
      method = response.model?.data?.link?.method as any;
    }

    // フォームエレメントの生成
    const form: HTMLFormElement = document.createElement('form');

    // フォームの設定
    form.action = url;
    form.method = method;
    form['rel'] = 'opener';
    form.className = 'amopform';
    form.target = 'amopTab';

    const myElement: any = document.querySelector('asw-service-request-payment-input-cont');

    // フォームをiframeの内側に挿入する
    myElement?.insertAdjacentHTML('afterbegin', form.outerHTML);

    // iframe内のフォームのサブミットイベントをリッスンする
    myElement?.querySelector('form.amopform')?.addEventListener('formdata', (e: FormDataEvent) => {
      const fd: FormData = e.formData;

      // データをセット
      payload.forEach((item: UpdateServicesResponseDataLinkPayloadInner) => {
        if (item.value) {
          fd.set(item.name ?? '', item.value);
        }
      });
    });

    // Clear Storage
    localStorage.removeItem('amopStatus');

    // Create Amop Tab with amop name
    const amopTab: Window | null = window.open('', 'amopTab');

    if (amopTab === null) {
      this._loggerDatadogService.error(LogType.PAGE_VIEW, { error: 'タブが開けない' }, false);
    }

    // submit form
    myElement?.querySelector('form.amopform')?.submit();

    // Delete form
    myElement?.querySelector('form.amopform')?.remove();

    // Testing: polling child tab open/close status
    this.subscribeService('amopTabPolling', interval(1000), () => {
      // No Tab open
      if (amopTab === null) {
        this.deleteSubscription('amopTabPolling');
        this.deleteSubscription('amopPostMessage');
        this._isPaying$.next(false);
        // ローディング終了
        this._loadingSvc.endLoading();
        return;
      }

      // Tab is opened
      if (!amopTab.closed) {
        // Do nothing. Decide that payment is in progress
        this._loggerDatadogService.info(LogType.PAGE_VIEW, { status: 'open' }, false);
        return;
      }

      // Tab is closed
      if (amopTab.closed) {
        this.deleteSubscription('amopTabPolling');
        this.deleteSubscription('amopPostMessage');

        // Get status from local storage
        const amopStatus: string | null = localStorage.getItem('amopStatus');

        // Clear Storage
        localStorage.removeItem('amopStatus');

        if (amopStatus === null) {
          this._isPaying$.next(false);
          this._loadingSvc.endLoading();
        }
      }
    });
  }
  private _searchWarningsCode(warnings?: Array<CreateMycarValetResponseWarningsInner>, code?: string) {
    return warnings?.some((warnings) => warnings.code === code);
  }

  /**
   * Setup completion timer
   * @returns Timer subscription
   */
  private _cardinalClientTimeoutHandle(): Subscription {
    // (14.5.2.1.1.5)	取得したcardinalタイムアウトの値を経過した場合、以下の情報を設定する。
    const timeoutValuetemp = Number(this._aswMasterService.getMPropertyByKey('cardinal', 'timeout.millisecond.client'));
    const timeoutValue: number = isNaN(timeoutValuetemp) ? 0 : timeoutValuetemp;
    return timer(timeoutValue).subscribe(() => {
      (window as any).Cardinal.off('payments.setupComplete');
      this._common.errorsHandlerService.setNotRetryableError({
        errorType: ErrorType.BUSINESS_LOGIC,
        errorMsgId: 'E0398',
      });
      console.log(
        'Base.cors: Sending async post request to https://centinelapistag.cardinalcommerce.com/V1/Order/JWT/Init with a timeout value of ' +
          timeoutValue
      );
      this._loadingSvc.endLoading();
    });
  }

  /**
   * The process of handling after receiving the cardinal
   * @param setupCompleteData
   * @returns
   */
  async _cardinalResponseEvent(setupCompleteData: SetupCompleteData) {
    this._loggerDatadogService.info(LogType.PAGE_VIEW, { response: setupCompleteData }, false);
    const modulesValid = (): boolean => {
      // (14.5.2.1.1.7.1) Cardinal応答(setupCompleteData)/modulesの件数分繰り返し、
      const vaild = setupCompleteData.modules.find((data: any) => {
        // 当該modules/module=“CCA”(クレジットカード) かつ 当該modules/loaded=true(成功)となるmodulesが存在しない。
        return data.module === 'CCA' && data.loaded;
      });
      return !!vaild;
    };

    // (14.5.2.1.1.7.2)	Cardinal応答/sessionIdが存在しないまたは空欄
    if (!setupCompleteData.sessionId || !modulesValid()) {
      this._common.errorsHandlerService.setRetryableError(PageType.PAGE, {
        errorMsgId: 'E0398',
      });
      this._loadingSvc.endLoading();
      return;
    }

    // (14.5.2.1.1.8) Cardinal応答/sessionIdをCardinalセッションIDとして保持する。
    this._cardinalSessionId = setupCompleteData.sessionId;

    // (14.5.2.1.1.9)	サービス情報登録APIを呼び出す。（2回目）
    this.ordersPaymentRecordsReqParam = this.createUpdateServicesRequest2();

    // (14.5.2.1.1.9.1) リクエスト情報
    const response2: any = await this._serviceRequestPaymentInputService.postOrdersUpdateServicesPost(
      this.ordersPaymentRecordsReqParam
    );
    // (14.5.2.1.1.9.2.1)	正常終了の場合
    if (response2.model) {
      // (14.5.2.1.1.9.2.1.1)	サービス情報登録API応答(2回目).warningsの中に、code = "WBAZ000224"(3DSv1認証が必要) または "WBAZ000225"(3DSv2認証が必要)が含まれる場合、以下の処理を行う。
      if (
        this._searchWarningsCode(response2.model.warnings, ErrorCodeConstants.ERROR_CODES.WBAZ000224) ||
        this._searchWarningsCode(response2.model.warnings, ErrorCodeConstants.ERROR_CODES.WBAZ000225)
      ) {
        // <以下、3DS認証前処理> 3DS2 authentication preprocessing
        this._preCardinalAuthentication2(response2);
      } else {
        // (14.5.2.1.1.9.2.1.2)	上記以外の場合、
        const resultHandleCommonNo5 = this._serviceRequestPaymentInputService.handleCommonNo5(response2);
        // (14.5.2.1.1.9.2.1.2.3)	戻り値= -1(異常終了)の場合、当処理を終了する。
        if (resultHandleCommonNo5 === 0) {
          this._postCompletion(response2.model ?? {});
        } else {
          this._loadingSvc.endLoading();
          return;
        }
      }
    } else {
      // (14.5.2.1.1.9.2.2)	異常終了の場合
      const resultHandleCommonNo5 = this._serviceRequestPaymentInputService.handleCommonNo5(response2);
      // (14.5.2.1.1.9.2.2.2)	戻り値= -1(異常終了)の場合、当処理を終了する。
      if (resultHandleCommonNo5 === -1) {
        this._loadingSvc.endLoading();
        return;
      }
    }
  }

  // (座席指定の登録が失敗した旨)
  mapWarningSeatRegistrationFailed(
    flights: Array<Type1>,
    travelers: Array<Traveler>,
    warning: CreateMycarValetResponseWarningsInner,
    warningCode: string
  ): WarningMessageData[] {
    // 10.1.2.1.2.2.1.	当該warning.sourceからflightId、travelerIdを取得する。
    const warnings: Array<WarningMessageData> = [];

    const map = new Map<string, string>();

    const warnItems = (warning.source as any)?.items;

    if (Array.isArray(warnItems)) {
      // 10.1.2.1.2.2.1.	当該warning.sourceからflightId、travelerIdを取得する。
      for (const { flightId, travelerIds } of warnItems) {
        // 販売キャリアコードを空で作成する。
        let marketingAirlineCode = '';
        // 便番号を空で作成する。
        let marketingFlightNumber = '';
        // 搭乗者名を空で作成する。

        // 10.1.2.1.2.2.2.	flightIdが存在する場合、以下の処理を行う。
        if (flightId) {
          const findSegment = flights.find((flight) => flight.id === flightId);
          if (findSegment) {
            marketingAirlineCode = stringEmptyDefault(findSegment?.marketingAirlineCode);
            marketingFlightNumber = stringEmptyDefault(findSegment?.marketingFlightNumber);
          }
        }

        // 10.1.2.1.2.2.4.	プレースホルダにセグメント情報.marketingAirlineCode＋セグメント情報.marketingFlightNumber＋" "(空白)＋搭乗者名を設定する。
        const marketingCodeNumber = marketingAirlineCode + marketingFlightNumber;

        // 10.1.2.1.2.2.3.	travelerIdが存在する場合、以下の処理を行う。
        travelerIds?.forEach((travelerId: string) => {
          // 10.1.2.1.2.2.3.1.	PNR情報取得API応答.data.travelers[]の中に、id = travelerIdの条件で最初の搭乗者情報を取得する。
          const findTraveler = travelers.find((traveler) => traveler.id === travelerId);
          if (findTraveler) {
            // 10.1.2.1.2.2.3.2.	搭乗者情報が取得できた場合、	搭乗者名に搭乗者情報.names[0].firstName + " "(半角スペース) + 搭乗者情報.names[0].lastNameを追。設定する。
            const fullNameTraveler =
              (findTraveler.names?.[0]?.title ? findTraveler.names?.[0]?.title + ' ' : '') +
              (findTraveler.names?.[0]?.firstName ?? '') +
              ' ' +
              (findTraveler.names?.[0]?.lastName ?? '');

            const content = marketingCodeNumber + ' ' + fullNameTraveler;

            /**
             * 	ワーニングメッセージリストの内に、ワーニングメッセージIDが同じについて、"当該flight.marketingAirlineCode＋当該flight.marketingFlightNumber"のプレースホルダ
             * が同じである場合、搭乗者氏名を（カンマ＋半角スペース）の区切りで連結してプレースホルダをとして、1つのワーニングメッセージIDとしてまとめる
             * 例： W0444 ※{0}: プレースホルダ="NH110 Mr.TARO SORANO"
             * W0444 ※{0}: プレースホルダ="NH110 Mr.SABURO SORANO"
             * W0444 ※{0}: プレースホルダ="NH110 Mr.TARO SORANO、 Mr.SABURO SORANO"
             */

            if (map.has(marketingCodeNumber)) {
              const currentName = map.get(marketingCodeNumber);
              map.set(marketingCodeNumber, currentName + ', ' + fullNameTraveler);
            } else {
              map.set(marketingCodeNumber, content);
            }
          }
        });
      }
    }

    for (const value of map.values()) {
      warnings.push({
        apiErrorCode: warning?.code,
        interpolateParams: {
          '0': value,
        },
        messageId: warningCode,
      });
    }

    return warnings;
  }

  /**
   *
   * @param responseUpdateService response from the last call to the updateService API
   */
  private _informationTransferProcessing(responseUpdateService: UpdateServicesResponse) {
    const listWBAZ000355 = this.responseUpdateService?.warnings?.filter(
      (w) => w?.code === ErrorCodeConstants.ERROR_CODES.WBAZ000355
    );
    const warnings = [...parseEmptyArray(listWBAZ000355), ...parseEmptyArray(responseUpdateService.warnings)];

    const flights = [
      ...parseEmptyArray(
        this._serviceRequestPaymentInputService.pnr$?.value?.air?.bounds?.flatMap((bound) => bound?.flights as Type1[])
      ),
    ];
    const travelers = [...parseEmptyArray(this._serviceRequestPaymentInputService.pnr$?.value?.travelers)];
    // (14.7) 初期表示時に取得したPNR情報取得API応答を破棄する。
    this._serviceRequestPaymentInputService.pnr$.next({});
    // (14.8) 予約詳細(サービス)(S01-P033)の[全サービスの編集情報破棄処理]を行う。
    // clear store S01-P033 before go to S02-P030
    this._myBookingServiceService.onHandleCommonNo3();
    // (14.9) 以下の情報を引継ぎ情報として引き渡し、Ancillary購入完了(S02-P030)へ遷移する。
    // (14.9.1) 変数を作成する。
    let warningMessageList: WarningMessageData[] = [];
    // (14.9.2) ワーニングメッセージリストに対して、以下の項目を追加する。
    for (let warning of warnings) {
      const apiErrorCode = ERROR_MAPPING[warning.code];
      // ※下表に対応するエラーメッセージIDが存在しない場合は、スキップする。
      if (warning?.code === ErrorCodeConstants.ERROR_CODES.WBAZ000355) {
        warningMessageList.push(...this.mapWarningSeatRegistrationFailed(flights, travelers, warning, 'W0444'));
      } else if (!isStringEmpty(apiErrorCode)) {
        warningMessageList.push({
          apiErrorCode: warning.code,
          messageId: apiErrorCode,
        });
      } else if (warning?.code === ErrorCodeConstants.ERROR_CODES.WBAZ000253) {
        warningMessageList.push(...this.mapWarningSeatRegistrationFailed(flights, travelers, warning, 'E0443'));
      }
    }
    // (14.9.3) 以下の値をAncillary購入完了storeに保持する。Ancillary購入完了(S02-P030)へ遷移する。
    const storeParams: ServiceRequestCompletedState = this._createServiceRequestCompletedState(
      warningMessageList,
      responseUpdateService
    );
    this._loadingSvc.endLoading();
    this._serviceRequestCompletedStoreService.updateStateServiceRequestCompleted(storeParams);
    this._router.navigateByUrl(FEATURE_TO_ROUTE_MAPPING['S02-P030']);
  }

  /**
   * Object store for S02-P030
   * @param warningMessageList
   * @param responseUpdateService
   * @returns
   */
  private _createServiceRequestCompletedState(
    warningMessageList: WarningMessageData[],
    responseUpdateService: UpdateServicesResponse
  ) {
    return {
      nextAction: this._serviceRequestPaymentInputStoreService?.storeCurrentValue?.nextAction,
      warningMessageList: warningMessageList,
      paymentInfo: {
        purchaseResults: responseUpdateService?.data?.orderStatus,
        is3DSecureApplied: this.is3DSecureApplied,
        paymentMethod: this._serviceRequestPaymentInputService.paymentMethod$.value,
      },
    };
  }

  private _cardinalContinue(response2: ApiResponse) {
    let payload = '';
    const transactionId = response2.model?.data?.link?.payload?.find((o) => o.name === 'transactionId')?.value ?? '';
    if (this._searchWarningsCode(response2?.model?.warnings, ErrorCodeConstants.ERROR_CODES.WBAZ000224)) {
      // 3DSv1認証が必要である場合
      payload = response2.model?.data?.link?.payload?.find((item) => item.name === 'paReq')?.value ?? '';
    } else if (this._searchWarningsCode(response2?.model?.warnings, ErrorCodeConstants.ERROR_CODES.WBAZ000225)) {
      // 3DSv1認証が必要である場合
      payload = response2.model?.data?.link?.payload?.find((item) => item.name === 'cReq')?.value ?? '';
    }
    this._ngZone.run(() => {
      (window as any)?.Cardinal?.continue(
        'cca',
        {
          AcsUrl: response2.model?.data?.link?.href ?? '',
          Payload: payload,
        },
        {
          OrderDetails: {
            TransactionId: transactionId ?? '',
          },
        }
      );
    });
  }

  private _cardinalValidated(response2: ApiResponse) {
    this._ngZone.run(() => {
      // Cardinalのコールバック(payments.validated)により実行される処理>
      (window as any)?.Cardinal?.on(
        'payments.validated',
        /**
         * 1.1.28.3 3DS認証後処理 (コールバック関数)
         * @param decodedResponseData Cardinalから返却された値
         * @param jwt Cardinal APIサービスからの応答JWT
         */
        (decodedResponseData: CardinalResponse, responseJWT: string) => {
          this._ngZone.run(() => {
            (window as any)?.Cardinal?.off('payments.validated');
            // return Cardinal log
            this._loggerDatadogService.info(LogType.PAGE_VIEW, decodedResponseData, false);
            this._loggerDatadogService.info(LogType.PAGE_VIEW, { responseJWT: responseJWT }, false);
            // 3DS認証結果情報
            this.response3DS = {
              errorNumber: decodedResponseData.ErrorNumber,
              errorDescription: decodedResponseData.ErrorDescription,
              jwtToken: responseJWT,
              challengeCancel: decodedResponseData.Payment.ExtendedData.ChallengeCancel,
            };
            this._cardinalAuthorizationPostProcessing(response2);
          });
        }
      );
    });
  }

  // (共通No.4) Cardinal初期設定処理
  _handleCommonNo4(response: ApiResponse) {
    // (14.5.2.1.1.1) 以下の条件でASWDBの「プロパティ」テーブルからプロパティ.valueを取得し、cardinalタイムアウトとして保持する。
    const timeoutValue = this._aswMasterService.getMPropertyByKey('cardinal', 'timeout.millisecond.cardinal') ?? '';
    (window as any).Cardinal.configure({
      // Cardinal接続タイムアウトの時間
      timeout: timeoutValue,
      // Cardinalコンソールログ出力(off：コンソールへのロギングは有効にならない／on：トランザクション中に発生したことに関する情報の提供を受ける)
      logging: {
        level: this._aswMasterService.getMPropertyByKey('cardinal', 'logging.level') ?? '',
      },
      payment: {
        view: 'modal',
        framework: 'cardinal',
        displayLoading: true, // ロード画面表示
        displayExitButton: false, // 閉じるボタンを出さない
      },
    });

    let jwtToken = '';
    // Cardinal setup
    if (response.model?.data?.paymentRequest?.authorization?.paymentRedirectionContext?.encrypted3DSToken) {
      jwtToken = decodeURIComponent(
        window.atob(response.model?.data?.paymentRequest?.authorization?.paymentRedirectionContext?.encrypted3DSToken)
      );
    }

    (window as any).Cardinal.setup('init', {
      jwt: jwtToken,
    });
  }

  updateMessageURLWithParam(url: string, key: string, value: string): string {
    const urlObj: URL = new URL(url);
    const searchParams: URLSearchParams = urlObj.searchParams;

    // check has params
    if (searchParams.toString()) {
      searchParams.append(key, value);
    } else {
      searchParams.set(key, value);
    }

    return urlObj.toString();
  }

  destroy(): void {
    /* override abstract destroy function of base class */
  }
}
